/**
 * useShellDomainAttacker - the attacker's side of the shell, derived from the
 * roster: the profile shim, per-weapon memory, the armed weapon, the toggles,
 * the next shot's mode and the modifiers they add up to.
 *
 * A domain hook: it reads what it needs from the shell scope `s` and returns
 * what it declares (see DeathWatchSimulator). Hook order unchanged.
 * Extracted from DeathWatchSimulator.
 */
import { useState } from 'react';
import useShellAttackProfiles from './useShellAttackProfiles';
import useShellPerWeapon from './useShellPerWeapon';
import useShellAttackToggles from './useShellAttackToggles';
import useShellAttackModifiers from './useShellAttackModifiers';
import useShellArmedWeapon from './useShellArmedWeapon';
import useActorSwitchReset from './useActorSwitchReset';

/**
 * The mode the NEXT shot is fired in (v8.307.0) - today only 'suppressing'. Not a toggle: it is true for exactly one
 * attack and cleared when the dice leave the cup, and AttackToggleState must gain no keys (its 128-mask parity sweep
 * has to keep meaning 2^7). A switch of actor clears it too (2026-10-22, `useActorSwitchReset`).
 */
function useAttackMode(actorId) {
  const [attackMode, setAttackMode] = useState(null);
  useActorSwitchReset(actorId, () => setAttackMode(null));
  return [attackMode, setAttackMode];
}

/**
 * @param {object} s - the shell scope (the state domain's names).
 * @returns {object} the attacker's profiles, weapon, toggles and modifiers.
 *   Length: the hooks' argument and return records (declarative wiring).
 */
export default function useShellDomainAttacker(s) {
  const {
    selectedSlot,
    setSelectedSlot,
    setTargetLocation,
    roster,
    setRoster,
    actorId,
    setActorId,
    actor,
    updateCombatantState,
    rosterTabs,
    activeTab,
  } = s;
  // ---- Attacker state, DERIVED from the roster ----
  // The profile list is the roster itself; a scratch rack serves acting as
  // nobody. See `hooks/useShellAttackProfiles`.
  const { atkProfiles, localCurrentProfile, setAtkProfiles, importAtkProfiles, currentSlots } = useShellAttackProfiles({
    roster,
    setRoster,
    actor,
    actorId,
    setActorId,
    setSelectedSlot,
  });

  // Custom mods, overrides and fire mode, remembered per armed weapon (v8.223.0).
  const {
    perWeaponAttackState,
    setPerWeaponAttackState,
    armedWeaponKey,
    attackCustomMods,
    weaponOverrides,
    setAttackCustomMods,
    setWeaponOverrides,
  } = useShellPerWeapon(actor, selectedSlot);

  // The armed weapon: its plate, its overridden view, its magazine, and
  // the attacker resolved for it. See `hooks/useShellArmedWeapon`.
  const { slot, isMelee, effectiveSlot, actorAmmo, attackerCharStats, attackerSourceCharacter, hasHammerBlow } =
    useShellArmedWeapon({ actor, selectedSlot, currentSlots, weaponOverrides });

  // The toggles, with aim (and Guarded's and All-Out's halves) living on
  // the combatant as conditions. See `hooks/useShellAttackToggles`.
  const actorConds = actor?.state?.conditions;
  const { rawAttackToggles, setRawAttackToggles, attackToggles, setAttackToggles, engageCalledShot, clearCalledShot } =
    useShellAttackToggles({ actor, actorConds, updateCombatantState, setTargetLocation });

  const [attackMode, setAttackMode] = useAttackMode(actor?.id ?? null);
  /** The Controls switch: this viewer's next called shots carry no penalty. Local, like Hatred. */
  const [ignoreCalledShot, setIgnoreCalledShot] = useState(false);

  // The called shot's reduction, the target's snap and called exposures,
  // Suppressing Fire's -20 and the toggles' sum. See the hook.
  const { calledShotExposure, calledShotReduction, targetNoAim, hasBerserkCharge, effectiveCustomMods, attackMod } =
    useShellAttackModifiers({
      ignoreCalledShot,
      rosterTabs,
      activeTab,
      actor,
      isMelee,
      effectiveSlot,
      attackerCharStats,
      attackToggles,
      attackCustomMods,
      attackMode,
    });

  return {
    atkProfiles,
    localCurrentProfile,
    setAtkProfiles,
    importAtkProfiles,
    currentSlots,
    perWeaponAttackState,
    setPerWeaponAttackState,
    armedWeaponKey,
    attackCustomMods,
    weaponOverrides,
    setAttackCustomMods,
    setWeaponOverrides,
    slot,
    isMelee,
    effectiveSlot,
    actorAmmo,
    attackerCharStats,
    attackerSourceCharacter,
    hasHammerBlow,
    actorConds,
    rawAttackToggles,
    setRawAttackToggles,
    attackToggles,
    setAttackToggles,
    engageCalledShot,
    clearCalledShot,
    attackMode,
    setAttackMode,
    calledShotExposure,
    calledShotReduction,
    ignoreCalledShot,
    setIgnoreCalledShot,
    targetNoAim,
    hasBerserkCharge,
    effectiveCustomMods,
    attackMod,
  };
}
